// framework/src/common/schemas/collections/chatMessages.js
import { z } from "zod";

const AttachmentSchema = z.object({
    fileId:       z.string(),
    fileName:     z.string(),
    mimeType:     z.string().optional().default("application/octet-stream"),
    size:         z.number().nullable().optional().default(null),
    thumbnailUrl: z.string().nullable().optional().default(null),
});

const ReactionSchema = z.object({
    emoji:   z.string(),
    userIds: z.array(z.string()).default([]),
});

const MentionSchema = z.object({
    participantId:   z.string(),
    participantType: z.enum(["user", "agent", "contact"]),
});

const ChatMessageSchema = z.object({
    _id:            z.string().optional().describe("MongoDB ObjectId"),
    subscriptionId: z.string(),
    threadId:       z.string(),

    // ── Sender ────────────────────────────────────────────────────────────
    senderId:    z.string().nullable().optional().default(null), // userId | agentName | contactId
    senderType:  z.enum(["user", "agent", "contact", "system"]).default("system"),

    // ── Message role (drives renderer selection) ──────────────────────────
    role: z.enum([
        "user",           // human message bubble
        "assistant",      // AI message bubble
        "system",         // center-aligned system notice
        "tool_group",     // inline AI tool execution batch card
        "tool_progress",  // async skill / job card
        "presentation",   // Present tool output card
        "event",          // system events: member_added, member_removed, etc.
    ]),

    // ── Content ───────────────────────────────────────────────────────────
    content:      z.string().nullable().optional().default(null),
    contentType:  z.enum(["markdown", "text", "emoji_only"]).default("markdown"),

    // ── Rich content ──────────────────────────────────────────────────────
    attachments: z.array(AttachmentSchema).default([]),
    reactions:   z.array(ReactionSchema).default([]),
    mentions:    z.array(MentionSchema).default([]),

    // ── Type-specific metadata ────────────────────────────────────────────
    // tool_group:    { tools: [{ toolName, ui, params, status, result, icon, label }] }
    // tool_progress: { jobId, goal, agentName }
    // presentation:  { title, summary, type, spaceId, files, agentName }
    // event:         { eventType: 'member_added'|'member_removed'|..., actorId, targetId }
    metadata: z.record(z.any()).optional().default({}),

    // ── Search ────────────────────────────────────────────────────────────
    // Maintained by ChatService — plain text (no markdown) for full-text indexing
    searchableContent: z.string().nullable().optional().default(null),

    createdAt: z.coerce.date().optional(),
    editedAt:  z.coerce.date().nullable().optional().default(null),
});

export default ChatMessageSchema;
